import { assertCanHandle, isNil, isNilOrEmpty } from '@lucidlink/tscore';
import { AUXDATA_LENGTH } from '#block-store/types';
import { GetRequestStatsContext } from '#block-store/utils';
import { Logging } from '#common/logging';
import { withStats } from '#common/stats';
import { CryptoError, InternalError } from '#common/types';
import { canHandleAllErrors, createTextDecoder } from '#common/util';
import { deserializeAesValue } from '#crypto';
import { createAesCrypto } from '#crypto/node-forge';
import { BasePointerKind } from '#external-files/types';
import { HttpInvalidCheckSumError } from '#object-store/types';
import { IoStats } from './io-stats';
import { LogStoreStats } from './types';
export const createLogStore = (params) => {
    return new LogStore(params.nodeId, params.objectStore, params.httpStore, params.fileNodeManager, params.commonConfigKeyHelper, params.logStoreWasm, params.metaStore, params.bucketName, params.objectPrefix, params.keyValueHelpers, params.tupleWasm, params.statsFlusher);
};
class LogStore {
    objectStore;
    httpStore;
    fileNodeManager;
    commonConfigKeyHelper;
    logStoreWasm;
    metaStore;
    bucketName;
    objectPrefix;
    log = Logging.getLogger('LogStore');
    statistics = new LogStoreStats(this.log);
    aesCrypto = createAesCrypto();
    textDecoder = createTextDecoder();
    ioStats;
    constructor(nodeId, objectStore, httpStore, fileNodeManager, commonConfigKeyHelper, logStoreWasm, metaStore, bucketName, objectPrefix, keyValueHelpers, tupleWasm, statsFlusher) {
        this.objectStore = objectStore;
        this.httpStore = httpStore;
        this.fileNodeManager = fileNodeManager;
        this.commonConfigKeyHelper = commonConfigKeyHelper;
        this.logStoreWasm = logStoreWasm;
        this.metaStore = metaStore;
        this.bucketName = bucketName;
        this.objectPrefix = objectPrefix;
        this.ioStats = new IoStats(nodeId, keyValueHelpers, metaStore, tupleWasm, this.log, statsFlusher);
    }
    async get(request) {
        const context = new GetRequestStatsContext(request, this.statistics.get);
        let layout;
        try {
            layout = await this.readLayout(request);
        }
        catch (err) {
            assertCanHandle(err, canHandleAllErrors);
            context.onRequestFailed();
            throw err;
        }
        this.ioStats.incrementGetStats(layout.getObjectCount, layout.getObjectSize);
        const blocksPromises = this.scheduleFetchOperations(layout.blocks, layout.basePtr, request.cryptoKey);
        return blocksPromises.map((promise, index) => this.processBlockPromise(promise, index, layout, context));
    }
    put(request) {
        return withStats(this.statistics.put, async () => {
            const globalMetaValue = await this.fileNodeManager.getGlobalMetaValue(request.inodeId);
            const result = await this.logStoreWasm.preparePutOperation(request.inodeId, globalMetaValue, request.version, request.blocks);
            const dataKv = result.physicalObjects;
            await Promise.all(dataKv.map((kv) => this.putObject(kv.key, kv.value)));
            const bytesWritten = dataKv.reduce((sum, kv) => sum + kv.value.length, 0);
            this.ioStats.incrementPutStats(dataKv.length, bytesWritten);
            this.statistics.totalBytesWritten += bytesWritten;
            await this.commitMetadata(result);
            this.fillOutcome(request, result);
        }, { id: request.inodeId, blockCount: request.blocks.length, version: request.version });
    }
    stats() {
        return this.statistics;
    }
    stop() {
        return Promise.resolve();
    }
    async readLayout(request) {
        const layoutKvs = await this.fileNodeManager.getLayoutKeyRange(request.inodeId);
        return withStats(this.statistics.readLayout, async () => {
            return await this.logStoreWasm.readLayout(request.inodeId, layoutKvs, request.startBlockId, request.limitBlockId);
        }, { id: request.inodeId });
    }
    async commitMetadata(result) {
        for (const trasnaction of result.metadataTransactions) {
            await this.metaStore.commitKeyValuePairs(trasnaction, false);
        }
    }
    async processBlockPromise(blockPromise, blockIndex, layout, context) {
        const blockLayout = layout.blocks[blockIndex];
        try {
            const blockInfo = await this.awaitBlockPromise(blockPromise, blockLayout);
            this.updateGetStats(blockInfo);
            context.onGetSucceeded();
            return blockInfo.block;
        }
        catch (err) {
            assertCanHandle(err, canHandleAllErrors);
            context.onGetFailed();
            this.logBlockFetchFailure(err, blockLayout, layout, context);
            throw err;
        }
    }
    logBlockFetchFailure(err, blockLayout, layout, context) {
        this.log.error({
            fileId: context.request.inodeId,
            blockId: blockLayout.blockId,
            objectKey: blockLayout.physObjectId,
            basePointerKind: layout.basePtr.kind,
            size: blockLayout.size,
            reason: err,
        }, 'Failed to fetch block.');
    }
    updateGetStats(blockInfo) {
        const block = blockInfo.block;
        switch (blockInfo.basePointerKind) {
            case BasePointerKind.NONE:
                this.ioStats.incrementGetStats(1, block.data.auxBuffer.length + block.data.dataBuffer.length);
                break;
            case BasePointerKind.SINGLE_OBJECT:
                this.ioStats.incrementHttpGetStats(1, block.data.dataBuffer.length);
                break;
            case BasePointerKind.HTTP:
                this.ioStats.incrementHttpGetStats(1, block.data.dataBuffer.length);
                this.ioStats.incrementHttpFileGetStats(1, block.data.dataBuffer.length);
                break;
        }
    }
    async awaitBlockPromise(promise, layout) {
        return withStats(this.statistics.fetchedObjects, async () => {
            return await promise;
        }, { size: layout.size, id: layout.blockId });
    }
    putObject(key, value) {
        return withStats(this.statistics.putObject, () => this.objectStore.putObject(this.bucketName, key, value), {
            key,
            size: value.length,
        });
    }
    scheduleFetchOperations(blocksLayout, basePtr, cryptoKey) {
        const blocksFetchedPromises = [];
        for (const blockLayout of blocksLayout) {
            blocksFetchedPromises.push(this.scheduleFetchOperation(blockLayout, basePtr, cryptoKey));
        }
        return blocksFetchedPromises;
    }
    fillOutcome(request, result) {
        for (const blockVersion of result.blockVersionsOutcome) {
            request.outcome.blockVersions.push({
                blockId: blockVersion.blockId,
                version: blockVersion.version,
            });
        }
    }
    async scheduleFetchOperation(blockLayout, basePtr, cryptoKey) {
        if (!isNil(blockLayout.physObjectId)) {
            return this.fetchObjectStoreBlock(blockLayout);
        }
        switch (basePtr.kind) {
            case BasePointerKind.NONE:
                throw new InternalError(`Block ${blockLayout.blockId} has no physical object id and no base pointer`);
            case BasePointerKind.SINGLE_OBJECT:
                return this.fetchSingleObjectBlock(blockLayout, basePtr);
            case BasePointerKind.HTTP:
                return this.fetchHttpBlock(blockLayout, basePtr, cryptoKey);
        }
    }
    async fetchObjectStoreBlock(blockLayout) {
        const object = await this.objectStore.getObject(this.bucketName, this.createObjectKey(blockLayout.physObjectId), blockLayout.physOffset, blockLayout.size);
        this.statistics.totalBytesRead += object.length;
        return {
            block: this.createBlock(blockLayout.blockId, object),
            basePointerKind: BasePointerKind.NONE,
        };
    }
    async fetchSingleObjectBlock(blockLayout, basePtr) {
        const url = await this.decryptPresignedUrl(basePtr);
        const httpObject = await this.httpStore.getObject(url, blockLayout.physOffset, blockLayout.size);
        if (httpObject.checksum !== basePtr.checksum) {
            throw new HttpInvalidCheckSumError(`Expected checksum: ${basePtr.checksum}, actual checksum: ${httpObject.checksum}`);
        }
        this.statistics.totalBytesRead += httpObject.data.length;
        return {
            block: this.createBlockNoAux(blockLayout.blockId, httpObject.data),
            basePointerKind: basePtr.kind,
        };
    }
    async fetchHttpBlock(blockLayout, httpPointer, cryptoKey) {
        if (cryptoKey.length === 0) {
            throw new CryptoError('Missing crypto key');
        }
        const url = this.decryptHttpUrl(httpPointer, cryptoKey);
        const httpObject = await this.httpStore.getObject(url, blockLayout.physOffset, blockLayout.size);
        this.statistics.totalBytesRead += httpObject.data.length;
        return {
            block: this.createBlockNoAux(blockLayout.blockId, httpObject.data),
            basePointerKind: httpPointer.kind,
        };
    }
    async decryptPresignedUrl(singleObjectPointer) {
        const decrypted = await this.commonConfigKeyHelper.decryptCommon(singleObjectPointer.encryptedUrl, singleObjectPointer.iv);
        return this.textDecoder.decode(decrypted);
    }
    decryptHttpUrl(httpPointer, cryptoKey) {
        const aesValue = deserializeAesValue(httpPointer.encryptedUrl);
        const decrypted = this.aesCrypto.decrypt(aesValue, cryptoKey);
        return this.textDecoder.decode(decrypted);
    }
    createBlock(blockId, object) {
        return {
            blockId: blockId,
            data: {
                auxBuffer: object.subarray(0, AUXDATA_LENGTH),
                dataBuffer: object.subarray(AUXDATA_LENGTH),
            },
        };
    }
    createBlockNoAux(blockId, data) {
        return {
            blockId: blockId,
            data: {
                auxBuffer: new Uint8Array(AUXDATA_LENGTH),
                dataBuffer: data,
            },
        };
    }
    createObjectKey(physObjectId) {
        if (isNilOrEmpty(this.objectPrefix)) {
            return physObjectId;
        }
        return `${this.objectPrefix}/${physObjectId}`;
    }
}
